import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Alert, Typography } from 'antd';

const { Title } = Typography;

/** 注入於報表 HTML 的 </head> 前，覆寫常見「窄欄／手機欄寬」樣式，讓桌面預覽善用主內容區寬度 */
const REPORT_HOST_STYLE_BLOCK = `<style id="whatoflow-report-host-override">
  html, body {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    box-sizing: border-box;
  }
  body { padding: 16px !important; }
  body > * {
    width: 100% !important;
    max-width: none !important;
    box-sizing: border-box;
  }
  table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse;
  }
  img, svg, video {
    max-width: 100% !important;
    height: auto !important;
  }
  *[style*="max-width"] { max-width: 100% !important; }
</style>`;

function buildReportIframeDocument(rawHtml) {
  const hostHead = `<meta charset="utf-8">${REPORT_HOST_STYLE_BLOCK}`;
  const h = (rawHtml || '').trim();

  if (!h) {
    return `<!DOCTYPE html><html><head>${hostHead}</head><body></body></html>`;
  }

  if (/<\/head>/i.test(h)) {
    return h.replace(/<\/head>/i, `${hostHead}</head>`);
  }

  if (/<head[^>]*>/i.test(h)) {
    return h.replace(/<head([^>]*)>/i, `<head$1>${hostHead}`);
  }

  const htmlBody = h.match(/<html([^>]*)>\s*<body/i);
  if (htmlBody) {
    return h.replace(/<html([^>]*)>\s*<body/i, `<html$1><head>${hostHead}</head><body`);
  }

  if (/<html[^>]*>/i.test(h)) {
    return h.replace(/<html([^>]*)>/i, `<html$1><head>${hostHead}</head>`);
  }

  return `<!DOCTYPE html><html><head>${hostHead}</head><body>${h}</body></html>`;
}

function resizeIframeToContent(iframe) {
  if (!iframe?.contentDocument?.body) return;
  try {
    const doc = iframe.contentDocument;
    const h = Math.max(
      doc.documentElement?.scrollHeight || 0,
      doc.body.scrollHeight || 0
    );
    iframe.style.height = `${Math.max(h + 32, 320)}px`;
  } catch (_) {
    /* ignore */
  }
}

const ReportInstancePage = () => {
  const { id } = useParams();
  const [html, setHtml] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeSrc, setIframeSrc] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const opts = { headers, credentials: 'include' };
        const urls = [
          `/api/report-instances/${id}`,
          `/api/ReportInstances/${id}`
        ];
        let data = null;
        let lastErr = null;
        for (const url of urls) {
          const res = await fetch(url, opts);
          const text = await res.text();
          const trimmed = text.trimStart();
          if (!res.ok) {
            lastErr = new Error(text || res.statusText);
            continue;
          }
          if (trimmed.startsWith('<')) {
            lastErr = new Error(
              '伺服器回傳網頁而非 JSON（API 路徑可能不符或尚未部署含 report-instances 的後端）'
            );
            continue;
          }
          try {
            data = JSON.parse(text);
            break;
          } catch (parseErr) {
            lastErr = parseErr;
          }
        }
        if (!data) throw lastErr || new Error('無法載入報表資料');
        if (!cancelled) {
          setHtml(data.generatedHtml || '');
          setName(data.instanceName || '');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || '載入失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!html) {
      setIframeSrc('');
      return undefined;
    }
    const doc = buildReportIframeDocument(html);
    const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    setIframeSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [html]);

  const onIframeLoad = useCallback((e) => {
    resizeIframeToContent(e.target);
    window.setTimeout(() => resizeIframeToContent(e.target), 400);
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div
      className="report-instance-page-shell"
      style={{
        padding: 16,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      {name ? <Title level={4}>{name}</Title> : null}
      {iframeSrc ? (
        <iframe
          title={name || 'HTML report'}
          src={iframeSrc}
          onLoad={onIframeLoad}
          sandbox="allow-same-origin allow-downloads"
          style={{
            width: '100%',
            minHeight: 320,
            border: 'none',
            display: 'block',
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
          }}
        />
      ) : null}
    </div>
  );
};

export default ReportInstancePage;
